class ProductBundle extends HTMLElement {
  constructor() {
    super();
    this.bundleHandle = this.dataset.bundleHandle;
    this.variants = this.parseVariants();
    this.bundleInner = this.querySelector('.product-bundle__inner');
    this.bundleProducts = this.querySelector('.product-bundle__products');
  }

  parseVariants() {
    try {
      const variantsScript = this.querySelector('.product-bundle__variants-data');
      if (!variantsScript) {
        console.error('Variants data script not found');
        return [];
      }
      return JSON.parse(variantsScript.textContent);
    } catch (error) {
      console.error('Error parsing variants:', error);
      return [];
    }
  }

  connectedCallback() {
    this.loadProductBundleSection();
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Listen for variant changes from the main product
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateFromVariant(event.detail.variant);
      }
    });
  }
  
  loadProductBundleSection(variantId = null) {
    if (!this.bundleHandle) {
      console.error('No bundle handle found for product bundle');
      return;
    }
    
    // Show loading state
    if (this.bundleInner) {
      this.bundleInner.innerHTML = `
        <div class="product-bundle__loading">
          <div class="loading-spinner"></div>
        </div>
      `;
    }
    
    // Build the URL for the section rendering API using the bundle handle
    let url = `/products/${this.bundleHandle}?section_id=product-bundle`;
    
    // Add variant ID to the URL if provided
    if (variantId) {
      url += `&variant=${variantId}`;
    }
    
    // Create an abort controller for the fetch request
    const controller = new AbortController();
    const signal = controller.signal;
    
    // Set a timeout to abort the request if it takes too long
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    fetch(url, { signal })
      .then(response => response.text())
      .then(html => {
        // Parse the HTML response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Find the product bundle section in the response
        const newSection = doc.querySelector('.product-bundle-section');
        if (newSection && this.bundleInner) {
          // Update the entire inner content
          this.bundleInner.innerHTML = newSection.innerHTML;
          
          // Initialize any custom elements in the new content
          this.initializeCustomElements(this.bundleInner);
          
          console.log('Product bundle section updated successfully');
        } else {
          console.error('Product bundle section not found in response');
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted due to timeout');
        } else {
          console.error('Error fetching product bundle section:', error);
        }
        
        // Show error state
        if (this.bundleInner) {
          this.bundleInner.innerHTML = `
            <div class="product-bundle__error">
              <p>Unable to load product bundle information. Please try again later.</p>
            </div>
          `;
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });
  }
  
  findVariantIdByValues(optionValues) {
    if (!this.variants || this.variants.length === 0) {
      console.error('No variants available');
      return null;
    }
    
    // Find the variant that matches all the provided option values
    const matchingVariant = this.variants.find(variant => {
      return optionValues.every((value, index) => {
        return variant.options && 
               variant.options[index] && 
               variant.options[index] === value;
      });
    });
    
    if (matchingVariant) {
      // Set the variant ID in the input field
      const idInput = this.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = matchingVariant.id;
      }
      
      // Load the product bundle section with the found variant ID
      this.loadProductBundleSection(matchingVariant.id);
      
      return matchingVariant.id;
    }
    
    return null;
  }

  updateFromVariant(variant) {
    if (!variant) return;
    
    // Get the variant options
    const optionValues = [];
    if (variant.option1) optionValues.push(variant.option1);
    if (variant.option2) optionValues.push(variant.option2);
    if (variant.option3) optionValues.push(variant.option3);
    
    // Find the variant ID using the option values
    const variantId = this.findVariantIdByValues(optionValues);
    
    // If we found a variant ID, update the section
    if (variantId) {
      this.loadProductBundleSection(variantId);
    }
  }
  
  initializeCustomElements(container) {
    // Initialize any custom elements in the container
    if (window.customElements) {
      const customElements = container.querySelectorAll('product-form');
      customElements.forEach(element => {
        if (!element.initialized) {
          element.initialized = true;
          // Dispatch an event to initialize the element
          element.dispatchEvent(new CustomEvent('product-form:init', { bubbles: true }));
        }
      });
    }
  }
}

customElements.define('product-bundle', ProductBundle); 